// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 56192a34-eb8a-4dfc-8154-c60bfce1c5a9

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Tabs, Tab, Table, TableHead, TableRow,
  TableCell, TableBody, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Alert, Switch, CircularProgress, Tooltip,
  List, ListItemButton, ListItemText, Collapse, FormControlLabel, Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getConfig } from './config';
import { getIdToken } from './auth';
import { PICKLISTS, TAG_GROUPS, allFieldsFor } from './schema';

// ---------------------------------------------------------------------------
// Lists & Fields administration.
//
// The product rule is: every dropdown a user sees is DATA, not a hardcoded
// array. This screen is where that data is managed — picklists, tag groups,
// vendors, and the field definitions themselves.
//
// The reference API is being built in parallel, so this screen degrades to a
// read-only view of the CURRENT hardcoded lists in schema.js when the API is
// unreachable. That keeps the screen useful (and demoable) before the backend
// lands, and it doubles as documentation of exactly what has to be migrated.
// ---------------------------------------------------------------------------

const OPT = { navy: '#021F4B', blue: '#0D5B9A', grey: '#5C6B7A', head: 'sectionHeader.main' };
const HEAD_SX = { '& th': { fontWeight: 700, color: OPT.head } };
const PRIMARY_BTN = { bgcolor: OPT.blue, '&:hover': { bgcolor: OPT.navy } };

// Mirrors field_def.data_type in backend/sql/001_schema.sql.
const DATA_TYPES = [
  'text', 'textarea', 'number', 'currency', 'boolean', 'date', 'percent',
  'select', 'multiselect', 'rating', 'arrows', 'url', 'ref',
];

// Default tag-group colours, kept in step with 002_seed_reference.sql so the
// offline view looks like the real thing.
const TAG_GROUP_COLORS = {
  Cell: '#F8BBD0',
  'DR Tier': '#C8E6C9',
  'Managed By': '#B3E5FC',
  Other: '#E1BEE7',
};

const BLANK_PICKLIST_VALUE = { value: '', label: '', color: '', sortOrder: 0, active: true };
const BLANK_TAG_VALUE = { value: '', color: '' };
const BLANK_VENDOR = { name: '', website: '', phone: '', notes: '', contacts: [] };
const BLANK_FIELD = {
  key: '', label: '', dataType: 'text', section: 'Information', picklistKey: '',
  mandatory: false, sortOrder: 0,
};

// The API may return snake_case (straight from Postgres) or camelCase; accept
// both so the screen does not break on whichever convention lands.
const pick = (obj, ...names) => {
  for (const n of names) if (obj && obj[n] !== undefined && obj[n] !== null) return obj[n];
  return undefined;
};

function normalize(raw) {
  const r = raw || {};
  // The API returns `picklists` as a MAP keyed by picklist key, because that is
  // the shape the fact-sheet renderer wants, and `picklistList` as the array.
  // Calling .map() on the map threw, the screen caught it and fell back to
  // built-in defaults, and it never left that state: every list shown here was
  // the hardcoded seed rather than the tenant's real data, and every edit was
  // written into a fallback that is discarded. Prefer the array the API
  // provides, and tolerate either shape so an older API cannot reintroduce it.
  const rawPicklists = Array.isArray(r.picklistList) ? r.picklistList
    : Array.isArray(r.picklists) ? r.picklists
      : Object.values(r.picklists || {});
  const picklists = rawPicklists.map((p) => ({
    id: p.id,
    key: p.key,
    label: p.label || p.key,
    values: (p.values || []).map((v) => ({
      id: v.id,
      // Defaulted because dialogValid() trims every one of these: a single NULL
      // coming back from the API crashed the screen on open.
      value: v.value || '',
      label: v.label || v.value || '',
      color: v.color || '',
      sortOrder: pick(v, 'sortOrder', 'sort_order') || 0,
      active: pick(v, 'active') !== false,
      // The API deliberately does not expose another tenant's id here; it sends
      // `scope: 'global' | 'tenant'`. Reading `tenantId` alone meant this was
      // always null, so ScopeChip labelled a client's OWN values as
      // Optimum-maintained globals - telling them they could not edit rows they
      // in fact owned. Keep the id fallback for older payloads.
      tenantId: pick(v, 'tenantId', 'tenant_id') || (v.scope === 'tenant' ? true : null),
    })).sort((a, b) => a.sortOrder - b.sortOrder),
  }));
  const tagGroups = (r.tagGroups || r.tag_groups || []).map((g) => ({
    id: g.id,
    key: g.key,
    label: g.label || g.key,
    color: g.color || TAG_GROUP_COLORS[g.key] || '',
    values: (g.values || []).map((v) => ({
      id: v.id,
      value: v.value || '',
      color: v.color || '',
      // Same as picklist values above: the API sends `scope`, not an id.
      tenantId: pick(v, 'tenantId', 'tenant_id') || (v.scope === 'tenant' ? true : null),
    })),
  }));
  const vendors = (r.vendors || []).map((v) => ({
    id: v.id,
    name: v.name || '',
    website: v.website || '',
    phone: v.phone || '',
    notes: v.notes || '',
    contacts: (v.contacts || []).map((c) => ({
      id: c.id, name: c.name, email: c.email || '', phone: c.phone || '', role: c.role || '',
    })),
  }));
  const fieldDefs = (r.fieldDefs || r.field_defs || []).map((f) => ({
    id: f.id,
    entityType: pick(f, 'entityType', 'entity_type') || 'Application',
    key: f.key,
    label: f.label || f.key,
    dataType: pick(f, 'dataType', 'data_type') || 'text',
    section: f.section || 'Information',
    subsection: f.subsection || '',
    picklistKey: pick(f, 'picklistKey', 'picklist_key', 'picklist') || '',
    mandatory: !!f.mandatory,
    sortOrder: pick(f, 'sortOrder', 'sort_order') || 0,
    tenantId: pick(f, 'tenantId', 'tenant_id') || null,
  }));
  return { picklists, tagGroups, vendors, fieldDefs };
}

// Everything the app hardcodes today, shaped like the API response so the rest
// of the component does not need an "offline" code path beyond disabling edits.
function fallbackData() {
  const picklists = Object.keys(PICKLISTS).map((key) => ({
    id: `local:${key}`,
    key,
    label: key,
    values: PICKLISTS[key].map((v, i) => ({
      id: `local:${key}:${i}`, value: v, label: v, color: '', sortOrder: i, active: true, tenantId: null,
    })),
  }));

  const tagGroups = TAG_GROUPS.map((g) => ({
    id: `local:tag:${g}`,
    key: g,
    label: g,
    color: TAG_GROUP_COLORS[g] || '',
    // DR Tier is the one tag group whose values already exist as a picklist.
    values: g === 'DR Tier'
      ? (PICKLISTS.drTier || []).map((v, i) => ({ id: `local:tag:${g}:${i}`, value: v, color: '', tenantId: null }))
      : [],
  }));

  const fieldDefs = allFieldsFor('Application').map((f, i) => ({
    id: `local:field:${f.key}:${i}`,
    entityType: 'Application',
    key: f.key,
    label: f.label,
    dataType: f.type,
    section: f.section,
    subsection: '',
    picklistKey: typeof f.picklist === 'string' ? f.picklist : (Array.isArray(f.picklist) ? '(inline)' : ''),
    mandatory: !!f.mandatory,
    sortOrder: i,
    tenantId: null,
  }));

  return { picklists, tagGroups, vendors: [], fieldDefs };
}

const ScopeChip = ({ tenantId }) => (
  <Chip
    size="small"
    label={tenantId ? 'tenant' : 'global'}
    sx={{
      height: 18, fontSize: 11,
      bgcolor: tenantId ? '#E3F2FD' : '#ECEFF1',
      color: tenantId ? OPT.blue : OPT.grey,
    }}
  />
);

export default function ListsAdmin({ tenantId }) {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState({ picklists: [], tagGroups: [], vendors: [], fieldDefs: [] });
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false); // reference API unreachable -> read-only
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectedPicklist, setSelectedPicklist] = useState('');
  const [openVendor, setOpenVendor] = useState('');
  const [dialog, setDialog] = useState(null); // { kind, mode, item, parentKey }

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    // An error envelope is often an HTML page, not JSON. Parsing it bare threw
    // SyntaxError here, before the !res.ok check below could report the real
    // status, so a 502 surfaced to the user as "Unexpected token <".
    let body = {};
    let parseFailed = false;
    try { body = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(body.error || body.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return body;
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
      const next = normalize(await api(`/reference${qs}`));
      setData(next);
      setOffline(false);
    } catch (e) {
      // Missing endpoint / network failure is expected until the reference API
      // ships — show the hardcoded lists rather than an empty screen.
      setData(fallbackData());
      setOffline(true);
    } finally {
      setLoading(false);
    }
  }, [api, tenantId]);

  useEffect(() => { load(); }, [load]);

  // Keep a picklist selected as the data changes underneath us.
  useEffect(() => {
    if (!data.picklists.length) return;
    if (!data.picklists.some((p) => p.key === selectedPicklist)) {
      setSelectedPicklist(data.picklists[0].key);
    }
  }, [data.picklists, selectedPicklist]);

  const activePicklist = useMemo(
    () => data.picklists.find((p) => p.key === selectedPicklist) || null,
    [data.picklists, selectedPicklist],
  );

  const fieldsBySection = useMemo(() => {
    const groups = new Map();
    [...data.fieldDefs]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .forEach((f) => {
        const key = f.section || 'Information';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(f);
      });
    return Array.from(groups.entries());
  }, [data.fieldDefs]);

  const readOnly = offline;

  // --- mutations ----------------------------------------------------------
  const savePicklistValue = async (picklistKey, value) => {
    await api('/reference/picklist-value', {
      method: 'PUT',
      body: JSON.stringify({ ...value, picklistKey, tenantId: tenantId || null }),
    });
  };

  const withSave = async (fn, successMessage) => {
    setBusy(true); setErr(''); setMsg('');
    try {
      await fn();
      setMsg(successMessage);
      setDialog(null);
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitDialog = () => {
    if (!dialog) return;
    const { kind, item, parentKey } = dialog;
    if (kind === 'picklistValue') {
      withSave(() => savePicklistValue(parentKey, item), `Saved “${item.label || item.value}”`);
    } else if (kind === 'tagValue') {
      withSave(
        () => api('/reference/tag-value', {
          method: 'PUT',
          body: JSON.stringify({ ...item, tagGroupKey: parentKey, tenantId: tenantId || null }),
        }),
        `Saved tag “${item.value}”`,
      );
    } else if (kind === 'vendor') {
      withSave(
        () => api('/reference/vendor', { method: 'PUT', body: JSON.stringify(item) }),
        `Saved vendor “${item.name}”`,
      );
    } else if (kind === 'field') {
      withSave(
        () => api('/reference/field-def', {
          method: 'PUT',
          body: JSON.stringify({ ...item, entityType: item.entityType || 'Application', tenantId: tenantId || null }),
        }),
        `Saved field “${item.label}”`,
      );
    }
  };

  const removePicklistValue = (picklistKey, value) => {
    if (!window.confirm(`Delete “${value.label || value.value}” from ${picklistKey}?`)) return;
    withSave(
      () => api(`/reference/picklist-value?id=${encodeURIComponent(value.id)}`, { method: 'DELETE' }),
      `Deleted “${value.label || value.value}”`,
    );
  };

  const removeTagValue = (groupKey, value) => {
    if (!window.confirm(`Remove tag “${value.value}” from ${groupKey}?`)) return;
    withSave(
      () => api(`/reference/tag-value?id=${encodeURIComponent(value.id)}`, { method: 'DELETE' }),
      `Removed tag “${value.value}”`,
    );
  };

  const removeVendor = (vendor) => {
    if (!window.confirm(`Delete vendor “${vendor.name}”? Applications referencing it will lose the link.`)) return;
    withSave(
      () => api(`/reference/vendor?id=${encodeURIComponent(vendor.id)}`, { method: 'DELETE' }),
      `Deleted vendor “${vendor.name}”`,
    );
  };

  // Reorder is a swap of sort_order between neighbours — two upserts, no
  // dedicated endpoint needed.
  const movePicklistValue = (picklistKey, index, delta) => {
    const list = activePicklist ? activePicklist.values : [];
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    const a = list[index];
    const b = list[target];
    withSave(async () => {
      await savePicklistValue(picklistKey, { ...a, sortOrder: b.sortOrder });
      await savePicklistValue(picklistKey, { ...b, sortOrder: a.sortOrder });
    }, 'Reordered');
  };

  const togglePicklistValueActive = (picklistKey, value) => {
    withSave(
      () => savePicklistValue(picklistKey, { ...value, active: !value.active }),
      `${value.active ? 'Deactivated' : 'Activated'} “${value.label || value.value}”`,
    );
  };

  const setItem = (patch) => setDialog((d) => ({ ...d, item: { ...d.item, ...patch } }));

  const setContact = (idx, patch) => setDialog((d) => {
    const contacts = d.item.contacts.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    return { ...d, item: { ...d.item, contacts } };
  });

  // --- render helpers -----------------------------------------------------
  const sectionTitle = (text) => (
    <Typography variant="subtitle2" sx={{ color: OPT.head, fontWeight: 700 }}>{text}</Typography>
  );

  const renderPicklists = () => (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Paper sx={{ width: 260, flexShrink: 0, maxHeight: 560, overflowY: 'auto' }}>
        <Box sx={{ px: 2, py: 1.25 }}>{sectionTitle('Picklists')}</Box>
        <List dense disablePadding>
          {data.picklists.map((p) => (
            <ListItemButton
              key={p.key}
              selected={p.key === selectedPicklist}
              onClick={() => setSelectedPicklist(p.key)}
              sx={{ '&.Mui-selected': { bgcolor: '#E8F1F9' } }}
            >
              <ListItemText
                primary={p.label}
                secondary={p.key}
                primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                secondaryTypographyProps={{ fontSize: 11, color: OPT.grey }}
              />
              <Chip size="small" label={p.values.length} sx={{ height: 18, fontSize: 11 }} />
            </ListItemButton>
          ))}
          {!data.picklists.length && (
            <Box sx={{ p: 2, color: '#9AA6B2', fontSize: 13 }}>No picklists defined.</Box>
          )}
        </List>
      </Paper>

      <Paper sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.25 }}>
          {sectionTitle(activePicklist ? `${activePicklist.label} — values` : 'Values')}
          <Box sx={{ flex: 1 }} />
          <Button
            size="small" startIcon={<AddIcon />} variant="contained" sx={PRIMARY_BTN}
            disabled={readOnly || !activePicklist}
            onClick={() => setDialog({
              kind: 'picklistValue', mode: 'create', parentKey: activePicklist.key,
              item: { ...BLANK_PICKLIST_VALUE, sortOrder: activePicklist.values.length },
            })}
          >
            Add value
          </Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow sx={HEAD_SX}>
              <TableCell>Value</TableCell>
              <TableCell>Label</TableCell>
              <TableCell align="right">Sort</TableCell>
              <TableCell>Colour</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activePicklist && activePicklist.values.map((v, i) => (
              <TableRow key={v.id || v.value} hover>
                <TableCell sx={{ fontWeight: 600 }}>{v.value}</TableCell>
                <TableCell>{v.label}</TableCell>
                <TableCell align="right" sx={{ color: OPT.grey }}>{v.sortOrder}</TableCell>
                <TableCell>
                  {v.color ? (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: v.color, border: '1px solid #CFD8DC' }} />
                      <span style={{ fontSize: 12, color: OPT.grey }}>{v.color}</span>
                    </Stack>
                  ) : <span style={{ color: '#9AA6B2' }}>—</span>}
                </TableCell>
                <TableCell><ScopeChip tenantId={v.tenantId} /></TableCell>
                <TableCell align="center">
                  <Switch
                    size="small" checked={!!v.active} disabled={readOnly}
                    onChange={() => togglePicklistValueActive(activePicklist.key, v)}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Move up">
                    <span>
                      <IconButton size="small" disabled={readOnly || i === 0}
                        onClick={() => movePicklistValue(activePicklist.key, i, -1)}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton size="small" disabled={readOnly || i === activePicklist.values.length - 1}
                        onClick={() => movePicklistValue(activePicklist.key, i, 1)}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <span>
                      <IconButton size="small" disabled={readOnly}
                        onClick={() => setDialog({ kind: 'picklistValue', mode: 'edit', parentKey: activePicklist.key, item: { ...v } })}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <span>
                      <IconButton size="small" disabled={readOnly}
                        onClick={() => removePicklistValue(activePicklist.key, v)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {(!activePicklist || !activePicklist.values.length) && (
              <TableRow>
                <TableCell colSpan={7} sx={{ color: '#9AA6B2', textAlign: 'center', py: 3 }}>
                  No values in this list yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );

  const renderTags = () => (
    <Box>
      <Typography variant="caption" sx={{ display: 'block', color: OPT.grey, mb: 2 }}>
        Tags are picked from these managed lists rather than typed free-form. That is what keeps
        “Clinical Apps” from becoming “clinical apps”, “Clinical App” and “Clinical-Apps” across
        three different engagements — and it is why every tag reliably shows up as a facet.
      </Typography>

      <Stack spacing={2}>
        {data.tagGroups.map((g) => (
          <Paper key={g.key} sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: g.color || '#ECEFF1', border: '1px solid #CFD8DC' }} />
              {sectionTitle(g.label)}
              <Chip size="small" label={`${g.values.length} value${g.values.length === 1 ? '' : 's'}`} sx={{ height: 18, fontSize: 11 }} />
              <Box sx={{ flex: 1 }} />
              <Button
                size="small" startIcon={<AddIcon />} variant="outlined" disabled={readOnly}
                onClick={() => setDialog({ kind: 'tagValue', mode: 'create', parentKey: g.key, item: { ...BLANK_TAG_VALUE, color: g.color || '' } })}
              >
                Add tag
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {g.values.map((v) => (
                <Chip
                  key={v.id || v.value}
                  size="small"
                  // Nesting a Chip inside a Chip label is invalid markup, so
                  // tenant-scoped values are signalled with a border instead.
                  label={v.value}
                  title={v.tenantId ? 'tenant value' : 'global value'}
                  onDelete={readOnly ? undefined : () => removeTagValue(g.key, v)}
                  sx={{
                    bgcolor: v.color || g.color || '#ECEFF1',
                    color: OPT.navy,
                    border: v.tenantId ? `1px solid ${OPT.blue}` : '1px solid transparent',
                  }}
                />
              ))}
              {!g.values.length && (
                <Typography variant="body2" sx={{ color: '#9AA6B2' }}>No values in this group yet.</Typography>
              )}
            </Stack>
          </Paper>
        ))}
        {!data.tagGroups.length && (
          <Paper sx={{ p: 3, color: '#9AA6B2', textAlign: 'center' }}>No tag groups defined.</Paper>
        )}
      </Stack>
    </Box>
  );

  const renderVendors = () => (
    <Paper>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.25 }}>
        {sectionTitle('Vendors')}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small" startIcon={<AddIcon />} variant="contained" sx={PRIMARY_BTN} disabled={readOnly}
          onClick={() => setDialog({ kind: 'vendor', mode: 'create', item: { ...BLANK_VENDOR, contacts: [] } })}
        >
          New vendor
        </Button>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow sx={HEAD_SX}>
            <TableCell sx={{ width: 40 }} />
            <TableCell>Name</TableCell>
            <TableCell>Website</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell align="right">Contacts</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.vendors.map((v) => (
            <React.Fragment key={v.id || v.name}>
              <TableRow hover>
                <TableCell>
                  <IconButton size="small" onClick={() => setOpenVendor(openVendor === v.id ? '' : v.id)}>
                    {openVendor === v.id ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                  </IconButton>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{v.name}</TableCell>
                <TableCell>
                  {v.website
                    ? <a href={v.website} target="_blank" rel="noreferrer" style={{ color: OPT.blue }}>{v.website}</a>
                    : <span style={{ color: '#9AA6B2' }}>—</span>}
                </TableCell>
                <TableCell>{v.phone || <span style={{ color: '#9AA6B2' }}>—</span>}</TableCell>
                <TableCell align="right">
                  <Chip size="small" label={v.contacts.length} sx={{ height: 18, fontSize: 11 }} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit vendor & contacts">
                    <span>
                      <IconButton size="small" disabled={readOnly}
                        onClick={() => setDialog({ kind: 'vendor', mode: 'edit', item: { ...v, contacts: v.contacts.map((c) => ({ ...c })) } })}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete vendor">
                    <span>
                      <IconButton size="small" disabled={readOnly} onClick={() => removeVendor(v)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={6} sx={{ py: 0, borderBottom: openVendor === v.id ? undefined : 'none' }}>
                  <Collapse in={openVendor === v.id} unmountOnExit>
                    <Box sx={{ py: 1.5, pl: 5 }}>
                      <Typography variant="caption" sx={{ color: OPT.grey, fontWeight: 700 }}>CONTACTS</Typography>
                      {v.contacts.length ? (
                        <Table size="small" sx={{ mt: 0.5 }}>
                          <TableHead>
                            <TableRow sx={HEAD_SX}>
                              <TableCell>Name</TableCell>
                              <TableCell>Email</TableCell>
                              <TableCell>Phone</TableCell>
                              <TableCell>Role</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {v.contacts.map((c, i) => (
                              <TableRow key={c.id || `${v.id}-contact-${i}`}>
                                <TableCell>{c.name}</TableCell>
                                <TableCell>{c.email || '—'}</TableCell>
                                <TableCell>{c.phone || '—'}</TableCell>
                                <TableCell>{c.role || '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#9AA6B2', mt: 0.5 }}>
                          No contacts recorded.
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
          {!data.vendors.length && (
            <TableRow>
              <TableCell colSpan={6} sx={{ color: '#9AA6B2', textAlign: 'center', py: 3 }}>
                No vendors yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  const renderFields = () => (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: OPT.grey, flex: 1 }}>
          Field definitions drive the fact-sheet editor. Keys and data types are structural — changing
          them would orphan stored values — so only the presentation (label, section, order, mandatory)
          is editable here. Add a custom field to extend the schema for this tenant.
        </Typography>
        <Button
          size="small" startIcon={<AddIcon />} variant="contained" sx={PRIMARY_BTN} disabled={readOnly}
          onClick={() => setDialog({ kind: 'field', mode: 'create', item: { ...BLANK_FIELD } })}
        >
          New field
        </Button>
      </Stack>

      <Stack spacing={2}>
        {fieldsBySection.map(([section, fields]) => (
          <Paper key={section}>
            <Box sx={{ px: 2, py: 1.25 }}>{sectionTitle(section)}</Box>
            <Table size="small">
              <TableHead>
                <TableRow sx={HEAD_SX}>
                  <TableCell>Label</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell>Data type</TableCell>
                  <TableCell>Picklist</TableCell>
                  <TableCell align="right">Sort</TableCell>
                  <TableCell align="center">Mandatory</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((f) => (
                  <TableRow key={f.id || f.key} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{f.label}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: OPT.grey }}>{f.key}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={f.dataType} sx={{ height: 18, fontSize: 11 }} />
                    </TableCell>
                    <TableCell>
                      {f.picklistKey
                        ? <Chip size="small" label={f.picklistKey} sx={{ height: 18, fontSize: 11, bgcolor: '#E8F1F9', color: OPT.blue }} />
                        : <span style={{ color: '#9AA6B2' }}>—</span>}
                    </TableCell>
                    <TableCell align="right" sx={{ color: OPT.grey }}>{f.sortOrder}</TableCell>
                    <TableCell align="center">
                      {f.mandatory
                        ? <Chip size="small" label="required" color="warning" sx={{ height: 18, fontSize: 11 }} />
                        : <span style={{ color: '#9AA6B2' }}>—</span>}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit presentation">
                        <span>
                          <IconButton size="small" disabled={readOnly}
                            onClick={() => setDialog({ kind: 'field', mode: 'edit', item: { ...f } })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        ))}
        {!fieldsBySection.length && (
          <Paper sx={{ p: 3, color: '#9AA6B2', textAlign: 'center' }}>No field definitions.</Paper>
        )}
      </Stack>
    </Box>
  );

  const dialogTitle = () => {
    if (!dialog) return '';
    const verb = dialog.mode === 'create' ? 'Add' : 'Edit';
    if (dialog.kind === 'picklistValue') return `${verb} value — ${dialog.parentKey}`;
    if (dialog.kind === 'tagValue') return `${verb} tag — ${dialog.parentKey}`;
    if (dialog.kind === 'vendor') return `${verb} vendor`;
    return `${verb} field`;
  };

  const dialogValid = () => {
    if (!dialog) return false;
    const it = dialog.item;
    if (dialog.kind === 'picklistValue') return !!it.value.trim();
    if (dialog.kind === 'tagValue') return !!it.value.trim();
    if (dialog.kind === 'vendor') return !!it.name.trim();
    return !!it.label.trim() && !!it.key.trim();
  };

  const renderDialogBody = () => {
    if (!dialog) return null;
    const it = dialog.item;

    if (dialog.kind === 'picklistValue') {
      return (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField fullWidth size="small" label="Value (stored)" value={it.value}
              onChange={(e) => setItem({ value: e.target.value, label: it.label || e.target.value })} />
            <TextField fullWidth size="small" label="Label (shown)" value={it.label}
              onChange={(e) => setItem({ label: e.target.value })} />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField size="small" type="number" label="Sort order" sx={{ width: 140 }} value={it.sortOrder}
              onChange={(e) => setItem({ sortOrder: Number(e.target.value) })} />
            <TextField fullWidth size="small" label="Colour (hex)" placeholder="#0D5B9A" value={it.color}
              onChange={(e) => setItem({ color: e.target.value })} />
            <FormControlLabel
              control={<Switch size="small" checked={!!it.active} onChange={(e) => setItem({ active: e.target.checked })} />}
              label={<span style={{ fontSize: 13 }}>Active</span>}
            />
          </Stack>
        </>
      );
    }

    if (dialog.kind === 'tagValue') {
      return (
        <Stack direction="row" spacing={2}>
          <TextField fullWidth size="small" label="Tag value" value={it.value}
            onChange={(e) => setItem({ value: e.target.value })} />
          <TextField fullWidth size="small" label="Colour (hex)" placeholder="#F8BBD0" value={it.color}
            onChange={(e) => setItem({ color: e.target.value })} />
        </Stack>
      );
    }

    if (dialog.kind === 'vendor') {
      return (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField fullWidth size="small" label="Vendor name" value={it.name}
              onChange={(e) => setItem({ name: e.target.value })} />
            <TextField fullWidth size="small" label="Phone" value={it.phone}
              onChange={(e) => setItem({ phone: e.target.value })} />
          </Stack>
          <TextField fullWidth size="small" label="Website" placeholder="https://" sx={{ mb: 2 }} value={it.website}
            onChange={(e) => setItem({ website: e.target.value })} />
          <TextField fullWidth size="small" label="Notes" multiline minRows={2} sx={{ mb: 2 }} value={it.notes}
            onChange={(e) => setItem({ notes: e.target.value })} />

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            {sectionTitle('Contacts')}
            <Box sx={{ flex: 1 }} />
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => setItem({ contacts: [...it.contacts, { name: '', email: '', phone: '', role: '' }] })}>
              Add contact
            </Button>
          </Stack>
          {it.contacts.map((c, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <TextField size="small" label="Name" value={c.name} onChange={(e) => setContact(i, { name: e.target.value })} />
              <TextField size="small" label="Email" value={c.email} onChange={(e) => setContact(i, { email: e.target.value })} />
              <TextField size="small" label="Phone" value={c.phone} onChange={(e) => setContact(i, { phone: e.target.value })} />
              <TextField size="small" label="Role" placeholder="primary" value={c.role}
                onChange={(e) => setContact(i, { role: e.target.value })} />
              <IconButton size="small" onClick={() => setItem({ contacts: it.contacts.filter((_, j) => j !== i) })}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          {!it.contacts.length && (
            <Typography variant="body2" sx={{ color: '#9AA6B2' }}>No contacts yet.</Typography>
          )}
        </>
      );
    }

    // field
    const isNew = dialog.mode === 'create';
    return (
      <>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField fullWidth size="small" label="Label" value={it.label}
            onChange={(e) => setItem({ label: e.target.value })} />
          <TextField
            fullWidth size="small" label="Key" value={it.key} disabled={!isNew}
            helperText={isNew ? 'Stable identifier — cannot be changed later.' : 'Structural; not editable.'}
            onChange={(e) => setItem({ key: e.target.value })}
          />
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select fullWidth size="small" label="Data type" value={it.dataType} disabled={!isNew}
            onChange={(e) => setItem({ dataType: e.target.value })}>
            {DATA_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField select fullWidth size="small" label="Bound picklist" value={it.picklistKey || ''}
            disabled={it.dataType !== 'select' && it.dataType !== 'multiselect' && !isNew}
            onChange={(e) => setItem({ picklistKey: e.target.value })}>
            <MenuItem value="">(none)</MenuItem>
            {data.picklists.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
          </TextField>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField fullWidth size="small" label="Section" value={it.section}
            onChange={(e) => setItem({ section: e.target.value })} />
          <TextField size="small" type="number" label="Sort order" sx={{ width: 140 }} value={it.sortOrder}
            onChange={(e) => setItem({ sortOrder: Number(e.target.value) })} />
          <FormControlLabel
            control={<Checkbox size="small" checked={!!it.mandatory} onChange={(e) => setItem({ mandatory: e.target.checked })} />}
            label={<span style={{ fontSize: 13 }}>Mandatory</span>}
          />
        </Stack>
      </>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: OPT.navy }}>Lists &amp; Fields</Typography>
        <Chip size="small" label={tenantId ? `tenant: ${tenantId}` : 'global reference data'} />
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={load} title="Refresh"><RefreshIcon /></IconButton>
      </Stack>

      <Typography variant="body2" sx={{ color: OPT.grey, mb: 2 }}>
        Every dropdown in Primordia reads from this screen. Global values are Optimum-maintained and
        shared by all clients; tenant values are yours alone.
      </Typography>

      {offline && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The reference API is not yet available, so these are the built-in lists shipped with the
          app and changes cannot be saved. Everything shown here is exactly what the API will serve
          once <code>/reference</code> is live.
        </Alert>
      )}
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: '1px solid #E0E6ED', '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' } }}
      >
        <Tab label="Picklists" />
        <Tab label="Tags" />
        <Tab label="Vendors" />
        <Tab label="Fields" />
      </Tabs>

      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={26} /></Box>
      ) : (
        <>
          {tab === 0 && renderPicklists()}
          {tab === 1 && renderTags()}
          {tab === 2 && renderVendors()}
          {tab === 3 && renderFields()}
        </>
      )}

      <Dialog open={!!dialog} onClose={() => !busy && setDialog(null)} fullWidth maxWidth={dialog && dialog.kind === 'vendor' ? 'md' : 'sm'}>
        <DialogTitle sx={{ fontWeight: 700, color: OPT.navy }}>{dialogTitle()}</DialogTitle>
        <DialogContent dividers>{renderDialogBody()}</DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" sx={PRIMARY_BTN} onClick={submitDialog} disabled={busy || !dialogValid()}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
